import type { components } from '@trackercop/contracts';
import { formatUsd } from '@/lib/format';

type CostSnapshotSummary = components['schemas']['CostSnapshotSummary'];

interface CostHistoryChartProps {
  history: CostSnapshotSummary[];
}

const BAR_HEIGHT = 160;
const BAR_GAP = 12;
const BAR_WIDTH = 56;
const PADDING_X = 16;
const PADDING_Y = 24;

/**
 * 6-month cost history bars. Plain SVG; we deliberately avoid pulling
 * in d3-scale or a chart library for one bar set (CLAUDE.md "D3
 * modules are opt-in" rule). Newest month is rightmost.
 */
export function CostHistoryChart({ history }: CostHistoryChartProps) {
  if (history.length === 0) {
    return (
      <div
        className="rounded-md border bg-background p-6 text-sm text-muted-foreground"
        data-testid="cost-history-empty"
      >
        No prior months yet — history accumulates after the first rollup tick.
      </div>
    );
  }

  // The repo returns newest first; chart renders oldest-to-newest.
  const sorted = [...history].reverse();
  const max = Math.max(...sorted.map((s) => s.projectedUsd), 1);
  const innerHeight = BAR_HEIGHT;
  const totalWidth = sorted.length * BAR_WIDTH + (sorted.length - 1) * BAR_GAP + PADDING_X * 2;
  const totalHeight = innerHeight + PADDING_Y * 2 + 32;

  return (
    <div
      className="overflow-x-auto rounded-md border bg-background p-4"
      data-testid="cost-history-chart"
    >
      <h3 className="mb-2 text-sm font-medium">Past months (projected)</h3>
      <svg
        width={totalWidth}
        height={totalHeight}
        viewBox={`0 0 ${totalWidth} ${totalHeight}`}
        role="img"
        aria-label="Monthly projected cost history"
      >
        {sorted.map((s, i) => {
          const barH = (s.projectedUsd / max) * innerHeight;
          const x = PADDING_X + i * (BAR_WIDTH + BAR_GAP);
          const y = PADDING_Y + (innerHeight - barH);
          return (
            <g key={s.month} data-testid={`cost-bar-${s.month}`}>
              <rect
                x={x}
                y={y}
                width={BAR_WIDTH}
                height={barH}
                rx={4}
                className="fill-primary/70"
              />
              <text
                x={x + BAR_WIDTH / 2}
                y={y - 4}
                textAnchor="middle"
                className="fill-foreground text-xs tabular-nums"
              >
                {formatUsd(s.projectedUsd, 0)}
              </text>
              <text
                x={x + BAR_WIDTH / 2}
                y={PADDING_Y + innerHeight + 16}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px] tabular-nums"
              >
                {s.month}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
